import React, { useRef, useState } from "react";
import { kcFetch, API_BASE, authHeaders } from "../../lib/apiClient";
import { useAppSettings, resolveLogo } from "../../lib/AppSettingsContext";
import { CustomIcon } from "../shared/CustomIcon";

// ============================================================
// FoodEntryDetailModal — single Food Contribution Registration
// entry view with download PDF, share link, print, and edit.
// Edit lets Donations team / Admins update household details
// (full custom fields preserved via raw JSON area as escape hatch).
// ============================================================
export default function FoodEntryDetailModal({ entry, onClose, onSaved, canEdit }) {
  const { settings: appSettings, loaded: appSettingsLoaded } = useAppSettings();
  const printRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...(entry.foodForm || {}) });
  const [busy, setBusy] = useState(false);
  const [shareMsg, setShareMsg] = useState("");

  // When the parent passes a fresh entry (after a save), reset the
  // editor's working copy so the rendered values reflect the latest data.
  React.useEffect(() => {
    setForm({ ...(entry?.foodForm || {}) });
  }, [entry?.id, entry?.foodForm]);

  if (!entry) return null;
  const ff = editing ? form : (entry.foodForm || {});
  const shareUrl = `${window.location.origin}/donations?need=${entry.id}`;

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const canvas = await html2canvas(printRef.current, { backgroundColor: "var(--kc-surface)", scale: 2, useCORS: true });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "px", format: [canvas.width, canvas.height] });
      pdf.addImage(img, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save(`Food-Registration-${(ff.lastName || "Entry")}-${entry.id}.pdf`);
    } finally { setBusy(false); }
  };

  const printOne = () => {
    const w = window.open("", "_blank", "noopener,noreferrer,width=820,height=900");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>${entry.description}</title>
      <link href="https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700&family=Montserrat:wght@400;600;700&display=swap" rel="stylesheet">
      <style>body{margin:0;background:var(--kc-surface);color:#FFF;font-family:'Montserrat',sans-serif;padding:24px;} .wrap{max-width:680px;margin:0 auto;}</style></head>
      <body><div class="wrap">${printRef.current.outerHTML}</div>
      <script>window.onload=function(){setTimeout(function(){window.print();},250);};</script></body></html>`);
    w.document.close();
  };

  const share = async () => {
    setShareMsg("");
    try {
      if (navigator.share) {
        await navigator.share({ title: entry.description, text: `${entry.description} — ${appSettings.appName}`, url: shareUrl });
        setShareMsg("Shared.");
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setShareMsg("Link copied to clipboard.");
      }
    } catch (_) {
      try { await navigator.clipboard.writeText(shareUrl); setShareMsg("Link copied."); } catch { setShareMsg("Copy failed."); }
    }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const updated = {
        description: entry.description,
        category: entry.category || "Food",
        urgency: entry.urgency || "Medium",
        status: entry.status || "Open",
        notes: entry.notes || "",
        dateAdded: entry.dateAdded || "",
        source: "food_form",
        foodForm: form,
      };
      const res = await kcFetch(`${API_BASE}/donations/needs/${entry.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(updated),
      });
      if (!res.ok) throw new Error("Save failed");
      const j = await res.json();
      onSaved && onSaved(j);
      setEditing(false);
    } catch (e) { window.alert(e.message || "Save failed"); }
    finally { setBusy(false); }
  };

  const Field = ({ label, value }) => (
    <div className="ffd-row">
      <div className="ffd-row-label">{label}</div>
      <div className="ffd-row-value">{value || "—"}</div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <style>{CSS}</style>
      <div className="modal ffd-modal" data-testid="food-entry-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Food Contribution Registration</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="ffd-toolbar">
            <button className="btn btn-outline btn-sm" data-testid="food-entry-download-pdf" onClick={downloadPdf} disabled={busy}>⬇ Download PDF</button>
            <button className="btn btn-outline btn-sm" data-testid="food-entry-share" onClick={share} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><CustomIcon name="link" size={14} /> Share</button>
            <button className="btn btn-outline btn-sm" data-testid="food-entry-print" onClick={printOne} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><CustomIcon name="printer" size={14} /> Print</button>
            {canEdit && !editing && <button className="btn btn-gold btn-sm" data-testid="food-entry-edit" onClick={() => setEditing(true)} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><CustomIcon name="edit" size={14} /> Edit</button>}
            {editing && <>
              <button className="btn btn-outline btn-sm" onClick={() => { setForm({ ...(entry.foodForm || {}) }); setEditing(false); }} disabled={busy}>Cancel</button>
              <button className="btn btn-gold btn-sm" data-testid="food-entry-save" onClick={saveEdit} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
            </>}
          </div>
          {shareMsg && <div className="ffd-share-msg" data-testid="food-entry-share-msg">{shareMsg}</div>}

          {/* Print/PDF body */}
          <div className="ffd-print" ref={printRef} data-testid="food-entry-print-region">
            <div className="ffd-header">
              <img src={resolveLogo(appSettings.appLogo)} alt={appSettings.appName} className="ffd-logo" />
              <div>
                <div className="ffd-brand">{appSettings.appName}</div>
                <div className="ffd-sub">KGC Compassion · Food Contribution Registration</div>
              </div>
            </div>
            <div className="ffd-tag">SUBMITTED {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : "—"}</div>

            <div className="ffd-section">
              <div className="ffd-section-title">Household Details</div>
              {editing ? (
                <>
                  <EditRow label="First Name" value={form.firstName || ""} onChange={v => setField("firstName", v)} testId="fed-firstName" />
                  <EditRow label="Last Name" value={form.lastName || ""} onChange={v => setField("lastName", v)} testId="fed-lastName" />
                  <EditRow label="Address" value={form.address || ""} onChange={v => setField("address", v)} testId="fed-address" />
                  <EditRow label="Phone (primary)" value={form.phonePrimary || ""} onChange={v => setField("phonePrimary", v)} testId="fed-phonePrimary" />
                  <EditRow label="Phone (secondary)" value={form.phoneSecondary || ""} onChange={v => setField("phoneSecondary", v)} testId="fed-phoneSecondary" />
                  <EditRow label="Dependents" value={form.dependents || ""} onChange={v => setField("dependents", v)} testId="fed-dependents" />
                </>
              ) : (
                <>
                  <Field label="First Name" value={ff.firstName} />
                  <Field label="Last Name" value={ff.lastName} />
                  <Field label="Address" value={ff.address} />
                  <Field label="Phone (primary)" value={ff.phonePrimary} />
                  <Field label="Phone (secondary)" value={ff.phoneSecondary} />
                  <Field label="Dependents" value={ff.dependents} />
                </>
              )}
            </div>

            <div className="ffd-section">
              <div className="ffd-section-title">Foods They Do Not Consume</div>
              <div className="ffd-chips" data-testid="food-entry-avoid">
                {(ff.avoidFoods || []).length === 0
                  ? <div className="ffd-empty">None specified.</div>
                  : (ff.avoidFoods || []).map(f => <span key={f} className="ffd-chip">{f}</span>)
                }
              </div>
            </div>

            {ff.custom && Object.keys(ff.custom).length > 0 && (
              <div className="ffd-section">
                <div className="ffd-section-title">Additional Details</div>
                {Object.entries(ff.custom).map(([k, v]) => (
                  <Field key={k} label={k} value={Array.isArray(v) ? v.join(", ") : String(v || "")} />
                ))}
              </div>
            )}

            <div className="ffd-footer">{appSettings.appTagline}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditRow({ label, value, onChange, testId }) {
  return (
    <div className="ffd-row">
      <div className="ffd-row-label">{label}</div>
      <input className="ffd-edit-input" data-testid={testId} value={value} onChange={e => onChange(e.target.value)} />
    </div>
  );
}

const CSS = `
.ffd-modal { max-width: 720px; }
.ffd-toolbar { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
.ffd-share-msg { background: rgba(74,222,128,0.12); border: 1px solid rgba(74,222,128,0.40); color: #4ade80; padding: 8px 12px; border-radius: 8px; font-size: 12px; margin-bottom: 10px; }
.ffd-print { background: #181818; border: 1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent); border-radius: 12px; padding: 22px; }
.ffd-header { display: flex; align-items: center; gap: 14px; padding-bottom: 14px; border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent); margin-bottom: 18px; }
.ffd-logo { width: 64px; height: 64px; object-fit: cover; border-radius: 10px; border: 1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent); }
.ffd-brand { font-family: 'Cinzel',serif; font-size: 18px; font-weight: 700; color: var(--kc-accent); letter-spacing: 0.06em; text-transform: uppercase; }
.ffd-sub { font-size: 11px; color: var(--kc-sub); letter-spacing: 0.08em; text-transform: uppercase; margin-top: 4px; }
.ffd-tag { font-size: 10px; color: var(--kc-meta); letter-spacing: 0.10em; text-transform: uppercase; margin-bottom: 18px; }
.ffd-section { margin-bottom: 22px; }
.ffd-section-title { font-family: 'Cinzel',serif; font-size: 13px; font-weight: 700; color: var(--kc-text); letter-spacing: 0.10em; text-transform: uppercase; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent); }
.ffd-row { display: grid; grid-template-columns: 180px 1fr; gap: 14px; padding: 7px 0; align-items: center; font-size: 13px; }
.ffd-row-label { color: var(--kc-sub); font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; font-weight: 700; }
.ffd-row-value { color: var(--kc-text); font-family: 'Montserrat',sans-serif; }
.ffd-edit-input { width: 100%; background: #141414; color: var(--kc-text); border: 1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent); border-radius: 8px; padding: 8px 12px; font-size: 13px; font-family: 'Montserrat',sans-serif; }
.ffd-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.ffd-chip { background: color-mix(in srgb, var(--kc-accent) 14%, transparent); border: 1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent); color: var(--kc-accent); padding: 4px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
.ffd-empty { color: var(--kc-meta); font-style: italic; font-size: 12px; }
.ffd-footer { margin-top: 18px; padding-top: 12px; border-top: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent); font-size: 11px; text-align: center; color: var(--kc-meta); letter-spacing: 0.08em; text-transform: uppercase; font-style: italic; }
`;
